import { useMutation } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  ApiException,
  ExternalPaymentMethodType,
  Feature,
  SaleLineItemCoverage,
} from 'src/WebApiController';

export type ApplyExternalPaymentInput = {
  externalPaymentMethodType: ExternalPaymentMethodType;
  externalPaymentNote?: string | null;
  externalPaymentAmount?: number | null;
  externalPaymentDate?: string | null;
  coverages?: SaleLineItemCoverage[] | null;
};

export type ApplyExternalPaymentRequest = {
  saleId: number;
  input: ApplyExternalPaymentInput;
};

export type ApplyExternalPaymentResponse = {
  saleId: number;
  marketplacePaymentState: number;
  pointOfSalePaymentState: number;
};

/**
 * Mutation hook to call POST /api/Sale/ApplyExternalPayment.
 * Used in Step 2 of the partial external payment flow: after an Unpaid sale is
 * created via CreateRetailSaleGroup (without external fields), the broker
 * selects coverages and this hook applies the external payment to the sale,
 * transitioning it to Paid or PartiallyPaid.
 *
 * Gated behind Feature.RetailSale to control access to external payment functionality.
 *
 * NOTE: This endpoint is not yet in the auto-generated SaleClient (pending Services
 * 29.20.1 publish). The hook uses a raw fetch call via transformOptions from
 * PosClientBase. Remove this hook and replace with SaleClient.applyExternalPayment
 * once the generated types are updated.
 */
export function useApplyExternalPayment() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();
  // Feature flag: gated behind RetailSale to control access to external payment functionality
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  return useMutation({
    mutationFn: async (
      request: ApplyExternalPaymentRequest
    ): Promise<ApplyExternalPaymentResponse> => {
      if (!hasRetailSaleFeature) {
        throw new Error('RetailSale feature is not enabled');
      }

      const { saleId, input } = request;
      const url = `/api/Sale/ApplyExternalPayment?saleId=${encodeURIComponent(saleId)}`;

      const init: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': navigator.language,
          ActiveAccountId: activeAccountWebClientConfig.activeAccountId ?? '',
        },
        body: JSON.stringify(input),
      };

      const response = await fetch(url, init);
      if (!response.ok) {
        const responseText = await response
          .text()
          .catch(() => response.statusText);
        throw new ApiException(
          `ApplyExternalPayment failed: ${response.status}`,
          response.status,
          responseText,
          {},
          null
        );
      }

      return response.json() as Promise<ApplyExternalPaymentResponse>;
    },
    onError: (error: ErrorTypes) => {
      trackError('SaleClient.applyExternalPayment', error);
      showErrorDialog('SaleClient.applyExternalPayment', error);
    },
  });
}
